import Auth from 'app/scripts/db/auth';
import { asString, isBrowserSupported } from '@trello/browser';
import { clientVersion } from '@trello/config';
import * as errorMemory from './errorMemory';

// eslint-disable-next-line @trello/enforce-package-boundaries
import * as Teams from '../../../teams.json';
type SentryTeam = keyof typeof Teams;

interface LegacyNavigator extends Navigator {
  browserLanguage: string;
}

interface Entry {
  name: string;
  msg: string;
  url?: string;
  line?: number;
  col?: number;
  stack?: string;
  ua: string;
  browser: string;
  isBrowserSupported: boolean;
  language: string;
  clientVersion: { head: string; version: number; patch: number };
  tags: {
    [key: string]: boolean | string;
  };
  idMember?: string;
}

export interface TagInfo {
  [key: string]: boolean | string;
  ownershipArea: SentryTeam;
}

interface ErrorInfo {
  error: Error;
  url?: string;
  line?: number;
  col?: number;
  tags?: TagInfo;
}

export const logError = async (
  error: Error | ErrorInfo,
  url?: string,
  line?: number,
  col?: number,
  tags?: TagInfo,
) => {
  const versionParts = clientVersion.split('-');
  const head = versionParts.slice(0, -1).join('-');
  const version = parseInt(versionParts.slice(-1)[0], 10);

  const errorSpread =
    error instanceof Error
      ? {
          stack: error.stack,
          name: error.name,
          msg: error.message,
          url,
          line,
          col,
          tags: { isBrowserSupported: isBrowserSupported(), ...tags },
        }
      : {
          stack: error.error.stack,
          name: error.error.name,
          msg: error.error.message,
          url: error.url,
          line: error.line,
          col: error.col,
          tags: { isBrowserSupported: isBrowserSupported(), ...error.tags },
        };

  const entry: Entry = {
    ua: navigator.userAgent,
    browser: asString,
    isBrowserSupported: isBrowserSupported(),
    language:
      navigator.language || (navigator as LegacyNavigator).browserLanguage,
    clientVersion: { head, version, patch: 0 },
    ...errorSpread,
  };

  if (Auth.isLoggedIn()) {
    entry.idMember = Auth.myId();
  }

  try {
    await fetch('/err', {
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': clientVersion,
      },
      credentials: 'include',
      method: 'POST',
      body: JSON.stringify({ errors: [entry] }),
    });

    errorMemory.add({
      timestamp: new Date().toISOString(),
      stack: entry.stack,
      name: entry.name,
      msg: entry.msg,
      url: entry.url,
    });
  } catch (err) {
    throw new Error(
      `There was a problem while logging an error: ${err.message}`,
    );
  }
};
